import RHFTextField from "$components/rhf/text-field";
import { Table, type TableActions } from "$components/table";
import { makeStyles, Tooltip } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import {
  Check as CheckIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
} from "@material-ui/icons";
import NotInterestedIcon from "@material-ui/icons/NotInterested";
import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
import classNames from "classnames";
import { clone } from "ramda";
import React, { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import { newLocationId } from "./constants";
import type {
  CreateLocationResponse,
  Location,
  LocationForm,
  LocationInput,
  UpdateLocationResponse,
} from "./types";

const useStyles = makeStyles((theme) => ({
  list: {
    margin: 0,
  },
  fullWidth: {
    width: "100%",
  },
  uppercase: { textTransform: "uppercase" },
  textGrey: { color: theme.palette.grey[600] },
  icon: {
    color: theme.palette.primary.dark,
    width: 24,
    height: 24,
    marginRight: "24px",
  },
  paddingRight: { paddingRight: 20 },
  deptLabel: { display: "flex", alignItems: "center" },
  deptDetails: { display: "flex", justifyContent: "space-between" },
  disabledCell: { opacity: 0.5 },
  flexCell: { flex: 1 },
}));

type FacilityLocationTableType = {
  createLocation: (location: LocationInput) => Promise<CreateLocationResponse>;
  updateLocation: (
    id: string,
    location: LocationInput,
    toggleLocation: boolean,
  ) => Promise<UpdateLocationResponse>;
  resetLocation: (index: number, location: Location) => void;
  cancelAddRow: (index: number) => void;
  loading: boolean;
  facilityLocations: Location[];
};

const FacilityLocationsTable = React.forwardRef(
  (
    {
      resetLocation,
      createLocation,
      updateLocation,
      cancelAddRow,
      loading,
      facilityLocations,
    }: FacilityLocationTableType,
    ref: React.MutableRefObject<TableActions>,
  ) => {
    const classes = useStyles();
    const { getValues, handleSubmit } = useFormContext<LocationForm>();
    const { formatMessage } = useIntl();

    const locations = getValues("locations").map((location) => ({
      id: location._id,
      ...location,
    }));

    const [editLocationIndex, setEditLocationIndex] = useState<number>(-1);
    const [creatingNewLocation, setCreatingNewLocation] =
      useState<boolean>(false);

    const handleSave = useCallback(
      async (index) => {
        setCreatingNewLocation(true);

        const {
          _id: id,
          name,
          active,
          bedCount,
        } = getValues(`locations.${index}` as "locations.0");

        const newLocationRow = id === newLocationId;

        let result: CreateLocationResponse | UpdateLocationResponse;

        if (newLocationRow) {
          result = await createLocation({
            name,
            bedCount: parseInt(bedCount, 10),
          });
        } else {
          result = await updateLocation(
            id,
            { name, active, bedCount: parseInt(bedCount, 10) },
            false,
          );
        }

        if (result) {
          ref.current.saveEditRow();
          setEditLocationIndex(-1);
        }

        setCreatingNewLocation(false);
      },
      [ref, getValues, createLocation, updateLocation],
    );

    const startEditRow = (index: number) => {
      if (editLocationIndex !== -1) {
        resetLocation(editLocationIndex, facilityLocations[editLocationIndex]);
      }

      ref.current.startEditRow(index);
      setEditLocationIndex(index);
    };

    const handleCancel = useCallback(
      (index) => {
        const prevLocationState = clone(facilityLocations[index]);

        if (prevLocationState) {
          resetLocation(index, prevLocationState);
        } else {
          cancelAddRow(index);
        }

        ref.current.cancelEditRow(index);
        setEditLocationIndex(-1);
      },
      [resetLocation, ref, cancelAddRow, facilityLocations],
    );

    const updateActivation = useCallback(
      async (index: number, active: boolean) => {
        const { _id: id, name } = getValues(
          `locations.${index}` as "locations.0",
        );

        await updateLocation(id, { name, active }, true);
      },
      [getValues, updateLocation],
    );

    return (
      ref && (
        <Table
          ref={ref}
          editing={{ onRowSave: async () => {} }}
          loading={loading}
          rows={locations}
          hover={false}
          noDataMessage={formatMessage({
            id: "facilityManagement.tableNoDataMessage",
            defaultMessage: "No locations found",
          })}
          columns={[
            {
              field: "name",
              headerName: "LOCATION",
              flex: 1,
              verticalAlignContents: "top",
              renderCell: (location: string, index) => {
                const isActive = locations[index]?.active;
                return (
                  <div
                    className={classNames({
                      [classes.disabledCell]: !isActive,
                    })}
                  >
                    {location}
                  </div>
                );
              },

              editCell: (_, index) => {
                return (
                  // div element necessary because otherwise the <Table /> component will overwrite the input’s name attribute
                  <div className={classes.flexCell}>
                    <RHFTextField
                      name={`locations.${index}.name`}
                      label={formatMessage({
                        id: "facilityLocations.nameField.label",
                        defaultMessage: "Location name",
                      })}
                      data-testid="facility-locations-location-input"
                      className={classes.fullWidth}
                    />
                  </div>
                );
              },
            },
            {
              field: "bedCount",
              headerName: "BEDS",
              flex: 1,
              verticalAlignContents: "top",
              renderCell: (location: string, index) => {
                const isActive = locations[index]?.active;
                return (
                  <div
                    className={classNames({
                      [classes.disabledCell]: !isActive,
                    })}
                  >
                    {location}
                  </div>
                );
              },

              editCell: (_, index) => {
                return (
                  // div element necessary because otherwise the <Table /> component will overwrite the input’s name attribute
                  <div className={classes.flexCell}>
                    <RHFTextField
                      name={`locations.${index}.bedCount`}
                      label={formatMessage({
                        id: "facilityLocations.bedCountField.label",
                        defaultMessage: "Beds",
                      })}
                      data-testid="facility-locations-location-bed-count-input"
                      className={classes.fullWidth}
                    />
                  </div>
                );
              },
            },
            {
              field: "active",
              width: 70,
              verticalAlignContents: "top",
              renderCell: (
                active: boolean,
                index: number,
                _: unknown,
                location: (typeof locations)[number],
              ) => {
                if (location.readOnly) {
                  return null;
                }

                return active ? (
                  <Tooltip
                    title={formatMessage({
                      id: "facilityLocations.disableLocation",
                      defaultMessage: "Disable location",
                    })}
                  >
                    <IconButton
                      className={classes.icon}
                      onClick={() => updateActivation(index, false)}
                      data-testid="facility-locations-disable-button"
                    >
                      <NotInterestedIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title={formatMessage({
                      id: "facilityLocations.activateLocation",
                      defaultMessage: "Activate location",
                    })}
                  >
                    <IconButton
                      className={classes.icon}
                      onClick={() => updateActivation(index, true)}
                      data-testid="facility-locations-activate-button"
                    >
                      <PlayCircleFilledIcon />
                    </IconButton>
                  </Tooltip>
                );
              },
              editCell: (_, index) => {
                return (
                  <Tooltip
                    title={formatMessage({
                      id: "facilityLocations.cancelChanges",
                      defaultMessage: "Cancel changes",
                    })}
                  >
                    <IconButton
                      className={classes.icon}
                      aria-controls="cancel"
                      onClick={() => handleCancel(index)}
                      data-testid="facility-locations-cancel-button"
                    >
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                );
              },
            },
            {
              field: "edit",
              width: 70,
              verticalAlignContents: "top",
              renderCell: (
                _,
                index,
                _col,
                location: (typeof locations)[number],
              ) => {
                if (location.readOnly) {
                  return null;
                }

                return (
                  <Tooltip
                    title={formatMessage({
                      id: "facilityLocations.editLocation",
                      defaultMessage: "Edit location",
                    })}
                  >
                    <IconButton
                      className={classes.icon}
                      aria-controls="edit"
                      onClick={() => {
                        startEditRow(index);
                      }}
                      data-testid="facility-locations-edit-button"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                );
              },
              editCell: (_, index) => {
                return (
                  <Tooltip
                    title={formatMessage({
                      id: "facilityLocations.saveChanges",
                      defaultMessage: "Save changes",
                    })}
                  >
                    <IconButton
                      className={classes.icon}
                      aria-controls="save"
                      disabled={creatingNewLocation}
                      onClick={handleSubmit(() => handleSave(index))}
                      data-testid="facility-locations-save-button"
                    >
                      <CheckIcon />
                    </IconButton>
                  </Tooltip>
                );
              },
            },
          ]}
        />
      )
    );
  },
);

export { FacilityLocationsTable };
